import React, { useRef, useState } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Permission, ResourceType } from "./entities";
import { TooltipButton } from "components/common/TooltipButton";
import { MenuItem } from "components/common/MenuItem";
import { DropdownMenu } from "components/common/DropdownMenu";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
interface AccessOption {
  label: string;
  value: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const USER_MODEL_ACCESS_OPTIONS: AccessOption[] = [
  {
    label: "Viewer",
    value: "Viewer",
    Icon: VisibilityOutlinedIcon,
  },
  {
    label: "Collaborator",
    value: "Collaborator",
    Icon: EditOutlinedIcon,
  },
];

const TABLE_ACCESS_OPTIONS: AccessOption[] = [
  {
    label: "Can Import",
    value: Permission.CanImport,
    Icon: FileDownloadOutlinedIcon,
  },
  { label: "Can Share", value: Permission.CanShare, Icon: ShareOutlinedIcon },
];

interface PermissionControlProps {
  currentAccess: string;
  resourceType: ResourceType;
  isDisabled?: boolean;
  isUpdating?: boolean;
  isCurrentUser?: boolean;
  showSuccess?: boolean;
  onAccessChange?: (newAccess: string) => void;
  onRemove?: () => void;
  onMenuToggle?: (isOpen: boolean) => void;
  entityName?: string;
  removeLabel?: string;
  skipRemoveConfirmation?: boolean;
}

interface AccessAction {
  label: string;
  value: string;
  Icon: React.ComponentType<{ className?: string }>;
  action: () => void;
}

export const PermissionControl: React.FC<PermissionControlProps> = ({
  currentAccess,
  resourceType,
  isDisabled = false,
  isUpdating = false,
  isCurrentUser = false,
  showSuccess = false,
  onAccessChange,
  onRemove,
  onMenuToggle,
  entityName,
  removeLabel = "Revoke Access",
  skipRemoveConfirmation = false,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleMenuOpen = () => {
    if (isDisabled || isUpdating || isCurrentUser || isRemoving) return;
    setMenuOpen(true);
    onMenuToggle?.(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    onMenuToggle?.(false);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const handleRemoveClick = () => {
    setIsConfirmingRemove(true);
    handleMenuClose();
  };

  const handleConfirmRevoke = async () => {
    if (!onRemove) return;
    setIsRemoving(true);
    try {
      await onRemove();
    } finally {
      setIsRemoving(false);
      setIsConfirmingRemove(false);
    }
  };

  // Datasets are flat — there is no role to show, so the borrowed relation
  // label (e.g. "Can Import") is suppressed; rows show only the revoke action.
  const displayAccess =
    resourceType === ResourceType.Dataset ? "" : currentAccess;

  const getAvailableOptions = (): AccessAction[] => {
    const options: AccessAction[] = [];

    if (onAccessChange) {
      const optionList =
        resourceType === ResourceType.Model
          ? USER_MODEL_ACCESS_OPTIONS
          : TABLE_ACCESS_OPTIONS;

      optionList
        .filter(
          (option) =>
            option.label.toLowerCase() !== currentAccess.toLowerCase(),
        )
        .forEach((option) => {
          options.push({
            label: `Make ${option.label}`,
            value: option.value,
            Icon: option.Icon,
            action: () => onAccessChange(option.value),
          });
        });
    }

    if (onRemove && !isCurrentUser) {
      options.push({
        label: removeLabel,
        value: "remove",
        Icon: PersonRemoveOutlinedIcon,
        action: skipRemoveConfirmation ? () => onRemove() : handleRemoveClick,
      });
    }

    return options;
  };

  if (isCurrentUser) {
    return (
      <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
        <span className="text-fg-muted-regular shrink-0">{displayAccess}</span>
        <div className="flex-none w-5 h-5" />
      </div>
    );
  }

  if (isDisabled) {
    return (
      <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
        <span className="text-fg-muted-medium shrink-0">{displayAccess}</span>
        <div className="flex-none w-5 h-5" />
      </div>
    );
  }

  const availableOptions = getAvailableOptions();

  if (availableOptions.length === 0) {
    return (
      <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
        <span className="text-fg-muted-medium shrink-0">{displayAccess}</span>
        <div className="flex-none w-5 h-5" />
      </div>
    );
  }

  const confirmMessage =
    resourceType === ResourceType.Table
      ? `Revoke access to this table${entityName ? ` from ${entityName}` : ""}? Their existing imports will become read-only stubs.`
      : `Revoke access${entityName ? ` for ${entityName}` : ""}? Their existing imports will become read-only stubs.`;

  return (
    <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
      {showSuccess && (
        <span className="flex items-center justify-center w-3 h-3 rounded-full border border-success mr-1">
          <CheckIcon className="text-success" style={{ fontSize: 6 }} />
        </span>
      )}
      <span className="text-fg-muted-regular">{displayAccess}</span>
      <div
        className={`flex-none transition-opacity duration-200 ${
          isMenuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        } ${isUpdating || isRemoving ? "opacity-40 pointer-events-none" : ""}`}
      >
        <TooltipButton
          ref={triggerRef}
          title="Actions"
          disableTooltip={isMenuOpen}
          variant="contextual"
          size="xs"
          onClick={isMenuOpen ? handleMenuClose : handleMenuOpen}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          className={isMenuOpen ? "button-active-state" : ""}
        >
          <MoreVertIcon />
        </TooltipButton>
      </div>

      <DropdownMenu
        anchorRef={triggerRef}
        isOpen={isMenuOpen}
        onClose={handleMenuClose}
        label="Actions"
        placement="bottom-end"
      >
        {availableOptions.map((option) => (
          <MenuItem
            key={option.value}
            Icon={option.Icon}
            label={option.label}
            onClick={(e) => {
              e.stopPropagation();
              if (option.value === "remove") {
                option.action();
              } else {
                handleAction(option.action);
              }
            }}
            disabled={isUpdating}
          />
        ))}
      </DropdownMenu>

      <ConfirmDialog
        open={isConfirmingRemove}
        title="Revoke access"
        message={confirmMessage}
        confirmText="Revoke"
        cancelText="Cancel"
        onConfirm={handleConfirmRevoke}
        onCancel={() => setIsConfirmingRemove(false)}
        isLoading={isRemoving}
      />
    </div>
  );
};
